<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useModal } from '@/composition/use-modal';
  import type { IMoveTarget } from '@/domain/catalog/move-targets';
  import V2CatalogTargetList from './V2CatalogTargetList.vue';

  /**
   * «Mover a otra categoría» (plan §5.9): the current menu's categories
   * without the products' own one. Resolves the chosen category id, or null.
   */
  const props = defineProps<{
    /** The vue-js-modal name it was opened with. */
    dialogName: string;
    title: string;
    targets: IMoveTarget[];
    branchName: string;
  }>();

  const modals = useModal();
  const root = ref<HTMLElement | null>(null);
  const selectedId = ref<number | null>(null);

  function answer(categoryId: number | null) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, categoryId);
  }

  onMounted(() => {
    root.value
      ?.querySelector<HTMLElement>('input, .target-option')
      ?.focus({ preventScroll: true });
  });
</script>

<template>
  <div
    ref="root"
    class="v2-catalog-move-dialog"
    role="dialog"
    :aria-label="title"
    @keydown.esc.stop="answer(null)"
  >
    <h2 class="move-title">{{ title }}</h2>
    <V2CatalogTargetList
      v-model="selectedId"
      class="move-targets"
      :targets="targets"
      :branch-name="branchName"
      :label="title"
      @pick="answer"
    />
    <div class="move-actions">
      <PosUiButton color="neutral" variant="ghost" @click="answer(null)">{{
        $t('cancel')
      }}</PosUiButton>
      <PosUiButton
        class="move-accept"
        :disabled="selectedId === null"
        @click="answer(selectedId)"
        >{{ $t('menuPage.move.cta') }}</PosUiButton
      >
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-move-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    .move-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .move-targets {
      @apply tw-mt-3;
    }

    // Margins, not flex gap (Chrome < 84).
    .move-actions {
      @apply tw-mt-4 tw-flex tw-items-center tw-justify-end;

      .move-accept {
        @apply tw-ml-2;
      }
    }
  }
</style>
